import { createContext, Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getPendingRequests, subscribeToRequests } from "../utils/api";

// Minimum time the refresh spinner stays visible (ms)
const MIN_REFRESH_MS = 800;

const PullRefreshContext = createContext({
    refreshing: false,
    refresh: () => {},
});

// Remounts everything below it when refresh() is called, so each page
// component re-runs its useEffect data loading (API calls).
export function PullRefreshProvider({ children }) {
    const [refreshKey, setRefreshKey] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const timerRef = useRef(null);
    const refreshLockRef = useRef(false);
    const startedAtRef = useRef(0);

    const finishWhenIdle = useCallback(() => {
        clearTimeout(timerRef.current);
        if (!refreshLockRef.current || getPendingRequests() > 0) return;

        const remaining = Math.max(0, MIN_REFRESH_MS - (Date.now() - startedAtRef.current));
        timerRef.current = setTimeout(() => {
            if (getPendingRequests() > 0) return;
            refreshLockRef.current = false;
            setRefreshing(false);
        }, remaining);
    }, []);

    useEffect(() => {
        const unsubscribe = subscribeToRequests(finishWhenIdle);
        return () => {
            unsubscribe();
            clearTimeout(timerRef.current);
        };
    }, [finishWhenIdle]);

    const refresh = useCallback(() => {
        // Prevent another remount while either the previous load or refresh
        // is in flight. The ref also guards multiple calls in the same event.
        if (refreshLockRef.current || getPendingRequests() > 0) return false;
        refreshLockRef.current = true;
        startedAtRef.current = Date.now();

        setRefreshing(true);
        setRefreshKey((k) => k + 1);

        finishWhenIdle();
        return true;
    }, [finishWhenIdle]);

    return (
        <PullRefreshContext.Provider value={{ refreshing, refresh }}>
            <Fragment key={refreshKey}>{children}</Fragment>
        </PullRefreshContext.Provider>
    );
}

export const usePullRefresh = () => useContext(PullRefreshContext);
